import React, { useState } from "react";
import { BottomSheetShell, PrimaryButton, statusStyle, GLYPH } from "./v2";
import { CLUB_STATUS_ORDER, clubStatusMeta, CLUB_STATUS_FILTERS, CLUB_SORT_OPTIONS } from "../../constants/clubRadarChoices";

// Mobile bottom sheets for the Club Radar (frames 1d/1e context — V2 rebuild on
// the kit's BottomSheetShell: scrim .44, r20 top, grab handle, pad 10/16/18).
// One BottomSheet primitive; each exported sheet is a thin content wrapper.
// Touch targets ≥44px. The artboard's 1e swipe row ships since R5 via the
// shared SwipeActionRow (components/common) — the swipe accelerators reuse
// THIS sheet's handlers (initialView deep-links Move/Copy into the picker
// views below), and the tap-to-open ⋯ sheet remains on every row as the
// gesture-free path. Presentation only: every sheet calls the same handlers
// as before.

export const BottomSheet = ({ title, subtitle, onClose, children }) => (
  <BottomSheetShell open onClose={onClose}>
    {title && <div style={{ fontSize: 15, fontWeight: 800, color: "#111827" }}>{title}</div>}
    {subtitle && <div style={{ fontSize: 11.5, color: "#9CA3AF", marginTop: 2 }}>{subtitle}</div>}
    <div style={{ marginTop: title ? 8 : 0 }}>{children}</div>
  </BottomSheetShell>
);

const SheetButton = ({ label, onClick, danger }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left cursor-pointer"
    style={{
      display: "block",
      minHeight: 48,
      padding: "13px 4px",
      fontSize: 14,
      fontWeight: 600,
      color: danger ? "#DC2626" : "#111827",
      background: "none",
      border: "none",
      borderBottom: "1px solid #F8FAFC",
      fontFamily: "inherit",
    }}
  >
    {label}
  </button>
);

export const MobileNewListSheet = ({ onCreate, onClose }) => {
  const [name, setName] = useState("");
  const submit = () => {
    const v = name.trim();
    if (v) onCreate(v);
  };
  return (
    <BottomSheet title="New list" onClose={onClose}>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && submit()}
        autoFocus
        placeholder="List name"
        maxLength={60}
        className="w-full outline-none"
        style={{ height: 44, border: "1px solid #E5E7EB", borderRadius: 11, padding: "0 12px", fontSize: 14, boxSizing: "border-box", fontFamily: "inherit" }}
      />
      <PrimaryButton height={46} radius={12} fontSize={14} style={{ width: "100%", marginTop: 14 }} onClick={submit} disabled={!name.trim()}>
        Create list
      </PrimaryButton>
    </BottomSheet>
  );
};

// Sort sheet (mobile). The status/scout-request chips and the Label filter now
// live inline in the 1e chip row per the artboard; sorting keeps its existing
// handler here, reachable from the "Sort ▾" chip.
export const MobileSortSheet = ({ sortBy, setSortBy, onClose }) => (
  <BottomSheet title="Sort" onClose={onClose}>
    {CLUB_SORT_OPTIONS.map((o) => (
      <SheetButton
        key={o.value}
        label={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {o.label}
            {sortBy === o.value && <span style={{ color: "#16A34A" }}>{GLYPH.check}</span>}
          </span>
        }
        onClick={() => { setSortBy(o.value); onClose(); }}
      />
    ))}
  </BottomSheet>
);

// Legacy combined Filter & sort sheet — superseded by the 1e inline chip row +
// MobileSortSheet (artboard defines the chips inline). Kept exported, not
// mounted: the handlers it wires (setFilter/setFilterLabel/setSortBy) all stay
// reachable in the rebuilt UI; nothing behind it was dropped.
export const MobileFilterSheet = ({ filter, setFilter, counts = {}, sortBy, setSortBy, labels = [], filterLabel, setFilterLabel, onClose }) => (
  <BottomSheet title="Filter & sort" onClose={onClose}>
    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#9CA3AF", margin: "4px 0 8px" }}>Status</div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
      {[...CLUB_STATUS_FILTERS, { value: "scout_requests", label: "From scout requests" }].map((f) => {
        const active = filter === f.value;
        return (
          <button key={f.value} type="button" onClick={() => setFilter(f.value)} className="cursor-pointer" style={{ minHeight: 40, padding: "0 13px", borderRadius: 999, fontSize: 13, fontWeight: active ? 700 : 600, border: active ? "1px solid #D6E7FF" : "1px solid #E5E7EB", background: active ? "#E8F1FF" : "#FFFFFF", color: active ? "#1B6FE0" : "#6B7280", fontFamily: "inherit" }}>
            {f.label}{counts[f.value === "all" ? "all" : f.value] != null ? ` ${GLYPH.middot} ${counts[f.value === "all" ? "all" : f.value]}` : ""}
          </button>
        );
      })}
    </div>
    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#9CA3AF", margin: "16px 0 8px" }}>Label</div>
    <select value={filterLabel} onChange={(e) => setFilterLabel(e.target.value)} className="w-full outline-none cursor-pointer" style={{ height: 46, border: "1px solid #E5E7EB", borderRadius: 11, padding: "0 12px", fontSize: 14, background: "#FFFFFF", fontFamily: "inherit" }}>
      <option value="all">All labels</option>
      {labels.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
    </select>
    <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#9CA3AF", margin: "16px 0 8px" }}>Sort</div>
    <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="w-full outline-none cursor-pointer" style={{ height: 46, border: "1px solid #E5E7EB", borderRadius: 11, padding: "0 12px", fontSize: 14, background: "#FFFFFF", fontFamily: "inherit" }}>
      {CLUB_SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
    <PrimaryButton height={46} radius={12} fontSize={14} style={{ width: "100%", marginTop: 16 }} onClick={onClose}>
      Done
    </PrimaryButton>
  </BottomSheet>
);

// Per-entry quick actions (frame 1e "Move"/"Remove" + status/copy). status
// submenu inline; dots use the artboard status hues (data-driven labels).
// `initialView` (R5, optional — default null keeps the old contract) lets the
// swipe accelerators open straight on the 'move'/'copy' list picker.
export const MobileEntryActionsSheet = ({ entry, lists, canManage, initialView = null, onOpen, onSetStatus, onCopy, onMove, onRemove, onClose }) => {
  const [view, setView] = useState(initialView); // 'status' | 'copy' | 'move'
  if (view === "status") {
    return (
      <BottomSheet title="Set status" onClose={onClose}>
        {CLUB_STATUS_ORDER.map((s) => (
          <SheetButton
            key={s}
            label={
              <span style={{ display: "inline-flex", alignItems: "center", gap: 9 }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: statusStyle(s).fg, display: "inline-block" }} />
                {clubStatusMeta(s).label}
                {entry.status === s ? <span style={{ color: "#16A34A", marginLeft: 4 }}>{GLYPH.check}</span> : null}
              </span>
            }
            onClick={() => { onSetStatus(entry, s); onClose(); }}
          />
        ))}
      </BottomSheet>
    );
  }
  if (view === "copy" || view === "move") {
    return (
      <BottomSheet title={view === "copy" ? "Copy to list" : "Move to list"} onClose={onClose}>
        {lists.filter((l) => l.id !== entry._listId).map((l) => (
          <SheetButton key={l.id} label={l.name} onClick={() => { (view === "copy" ? onCopy : onMove)(entry, l); onClose(); }} />
        ))}
      </BottomSheet>
    );
  }
  return (
    <BottomSheet title={entry.player?.name || entry.name} onClose={onClose}>
      <SheetButton label="Open details" onClick={() => { onOpen(entry); onClose(); }} />
      <SheetButton label={<>Set status{GLYPH.ellipsis}</>} onClick={() => setView("status")} />
      <SheetButton label={<>Copy to list{GLYPH.ellipsis}</>} onClick={() => setView("copy")} />
      {canManage && <SheetButton label={<>Move to list{GLYPH.ellipsis}</>} onClick={() => setView("move")} />}
      {canManage && <SheetButton label="Remove from list" danger onClick={() => { onRemove(entry); onClose(); }} />}
    </BottomSheet>
  );
};

// List ⋯ menu (mobile, role-aware — same gates as the desktop 1b/2d menu).
export const MobileListMenuSheet = ({ list, canManage, onRename, onEditDescription, onManageSharing, onShareToChat, onDelete, onClose }) => {
  const isDefault = !!list.is_default;
  return (
    <BottomSheet title={list.name} subtitle={list.created_by ? `created by ${list.created_by.is_me ? "you" : list.created_by.name}` : undefined} onClose={onClose}>
      {canManage && !isDefault && <SheetButton label="Rename list" onClick={() => { onRename(); onClose(); }} />}
      {canManage && !isDefault && <SheetButton label="Edit description" onClick={() => { onEditDescription(); onClose(); }} />}
      {canManage && !isDefault && <SheetButton label="Manage sharing" onClick={() => { onManageSharing(); onClose(); }} />}
      <SheetButton label="Share to chat" onClick={() => { onShareToChat(); onClose(); }} />
      {canManage && !isDefault && <SheetButton label="Delete list" danger onClick={() => { onDelete(); onClose(); }} />}
    </BottomSheet>
  );
};
